/**
 * 3D inspection viewport gate (front-page tx-3d section).
 *
 * three.js is heavy — the real viewer (product-3d-core.js, which
 * pulls three + addons) is a separate Vite chunk, dynamically
 * imported only when a [data-tx3d] section is on the page AND
 * scrolling brings it near the viewport.
 */
const gate = (root) => {
    let loaded = false;
    const load = () => {
        if (loaded) return;
        loaded = true;
        import('./product-3d-core.js')
            .then((mod) => mod.init(root))
            .catch(() => {
                const viewport = root.querySelector('[data-tx3d-viewport]');
                if (viewport) viewport.classList.add('is-err');
                root.dispatchEvent(new CustomEvent('tx3d:error'));
            });
    };

    if (!('IntersectionObserver' in window)) {
        load();
        return;
    }
    const io = new IntersectionObserver(
        (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                io.disconnect();
                load();
            }
        },
        { rootMargin: '600px 0px' }
    );
    io.observe(root);
};

const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
// A touch of overshoot: the card and the close button click into place.
const EASE_SNAP = 'cubic-bezier(0.22, 1.2, 0.36, 1)';
const EASE_IN = 'cubic-bezier(0.5, 0, 0.75, 0)';
const EASE_IN_OUT = 'cubic-bezier(0.65, 0, 0.35, 1)';

const mid = (r) => [r.left + r.width / 2, r.top + r.height / 2];
// Position and size that put an element whose containing box is `box`
// on rect `r`.
const placeAt = (box, r) => ({
    left: `${r.left - box.left}px`,
    top: `${r.top - box.top}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
});
// Offset that puts corner mark `c` (rect) on the matching corner of `r`,
// 14px in, as on the stage.
const cornerShift = (el, c, r) => {
    const right = /--(tr|br)/.test(el.className);
    const bottom = /--(bl|br)/.test(el.className);
    return [
        right ? r.right - 14 - c.right : r.left + 14 - c.left,
        bottom ? r.bottom - 14 - c.bottom : r.top + 14 - c.top,
    ];
};
// The page's scrollbar width (0 with overlay scrollbars, and for the
// layout viewport a too-wide page stretches on phones): while the view
// hides it, the page keeps its width (scss: html:has(.tx-sp3d[open])).
const scrollbar = () => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;top:-99px;width:50px;height:50px;overflow:scroll';
    document.body.appendChild(probe);
    const w = probe.offsetWidth - probe.clientWidth;
    probe.remove();
    return Math.max(0, Math.min(w, window.innerWidth - document.documentElement.clientWidth));
};

// Single product: a click on the hero model (the viewer's tx3d:expand)
// or Enter / Space on it opens the full-screen 3D view named by
// aria-controls, a native <dialog> (Escape, focus trapping and focus
// return come with showModal()). The hero's own viewer moves into it:
// the hero's blue drafting grid becomes the dialog's box over a fading-in
// navy veil, the model zooms from its place in the hero into the stage,
// and the key facts float in as a card. Closing runs it all back.
const wireModal = (root) => {
    const dialog = document.getElementById(root.getAttribute('aria-controls') || '');
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.dataset.txWired) return;
    dialog.dataset.txWired = '1';
    const heroView = root.querySelector('[data-tx3d-viewport]');
    const band = root.closest('.tx-sp__hero') || heroView;
    const stage = dialog.querySelector('.tx-sp3d__model');
    const port = dialog.querySelector('.tx-sp3d__viewport');
    const frame = dialog.querySelector('.tx-sp3d__frame');
    const bg = dialog.querySelector('.tx-sp3d__bg');
    const box = dialog.querySelector('.tx-sp3d__stage');
    const card = dialog.querySelector('.tx-sp3d__info');
    const closer = dialog.querySelector('.tx-sp3d__close');
    const hint = dialog.querySelector('.tx-sp3d__hint');
    const tools = dialog.querySelector('.tx-sp3d__tools');
    const corners = [...dialog.querySelectorAll('.tx-3d__corner')];
    const still = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let anims = [];
    let gen = 0;
    let closing = false;
    let openedBy = '';
    // Phones whose page is wider than the screen (the header, at 360-397px)
    // let the screen drift off the layout viewport the view is fixed to:
    // the view follows what is on screen.
    const vv = window.visualViewport;
    const pin = () => {
        if (vv) dialog.style.translate = `${vv.offsetLeft}px ${vv.offsetTop}px`;
    };

    const play = (el, frames, opts) => {
        if (!el) return null;
        const a = el.animate(frames, { fill: 'both', ...opts });
        anims.push(a);
        return a;
    };
    const stop = () => {
        anims.forEach((a) => a.cancel());
        anims = [];
    };
    // Once every running animation is done: drop them (the end state is
    // the element's own style) and carry on. Only the latest wait counts.
    const settle = (then) => {
        const batch = anims;
        const mine = ++gen;
        Promise.all(batch.map((a) => a.finished)).then(() => {
            if (mine !== gen) return;
            stop();
            then();
        }).catch(() => {});
    };
    // Lends the hero's canvas to the view, framed for the stage. The canvas
    // runs wider than the stage on both sides (transparent, clipped by the
    // box) so that, scaled down onto the hero at either end of the flight,
    // it still covers the whole hero viewport: no end of the model is cut.
    const lend = () => {
        const h = heroView.getBoundingClientRect();
        const bleed = Math.max(0, Math.ceil(((h.width / h.height) * stage.clientHeight - stage.clientWidth) / 2));
        port.style.left = `${-bleed}px`;
        port.style.right = `${-bleed}px`;
        root.tx3d.attach(port, stage);
    };
    // The viewer goes home to the hero, in the same frame the view goes.
    const home = () => {
        if (root.tx3d) root.tx3d.detach();
        port.style.left = '';
        port.style.right = '';
        root.classList.remove('is-expanded');
    };
    const below = () => card && box && card.getBoundingClientRect().top >= box.getBoundingClientRect().bottom - 1;

    const open = (e) => {
        if (dialog.open || heroView.classList.contains('is-err')) return;
        stop();
        closing = false;
        openedBy = e ? e.type : '';
        const v = root.tx3d;
        const from = heroView.getBoundingClientRect();
        const bandRect = band.getBoundingClientRect();
        document.documentElement.style.setProperty('--tx3d-scrollbar', `${scrollbar()}px`);
        dialog.classList.add('is-entering');
        dialog.showModal();
        // The veil starts clear and fades in.
        getComputedStyle(dialog, '::backdrop').opacity;
        dialog.classList.remove('is-entering');
        pin();
        if (vv) {
            vv.addEventListener('resize', pin);
            vv.addEventListener('scroll', pin);
        }
        // Opened by a click or tap: focus starts on the close button as
        // ever, minus the keyboard ring.
        if (e && e.type === 'tx3d:expand' && closer) {
            closer.blur();
            closer.focus({ focusVisible: false });
        }
        root.classList.add('is-expanded');
        if (frame) frame.scrollTop = 0;
        if (card) card.scrollTop = 0;
        if (v) lend();
        if (still()) return;
        dialog.classList.add('is-flying');

        // Measure everything before the first animation moves anything.
        const to = stage.getBoundingClientRect();
        const bgRect = bg && bg.getBoundingClientRect();
        const marks = corners.map((c) => [c, cornerShift(c, c.getBoundingClientRect(), from)]);
        const under = below();
        // The model leaves from the hero's exact view: the camera starts at
        // the hero's distance (follow() below), so the canvas only scales by
        // the two viewports' height ratio, and zooms out in step.
        let flight = null;
        if (v) {
            const [fx, fy] = mid(from);
            const [tx, ty] = mid(to);
            flight = `translate(${fx - tx}px, ${fy - ty}px) scale(${from.height / port.clientHeight})`;
        }

        // 1. The hero's blue becomes the box, its content fading under it
        // (the grid starts line on line with the hero's: both are drawn
        // from the top-left corner)...
        if (bgRect) {
            play(bg, [
                { ...placeAt(bgRect, bandRect), opacity: 0, borderColor: 'rgba(255, 255, 255, 0)' },
                { opacity: 1, offset: 0.3 },
                { ...placeAt(bgRect, bgRect), opacity: 1, borderColor: 'rgba(255, 255, 255, 0.16)' },
            ], { duration: 480, easing: EASE_OUT });
        }
        // 2. ...the model zooms from its place in the hero into the stage,
        // the corner marks locking on around it...
        if (flight) {
            const d = v.distances();
            v.follow(play(stage, [{ transform: flight }, { transform: 'none' }], { duration: 560, delay: 60, easing: EASE_OUT }), d.home, d.framed);
        } else {
            // No model yet: it shows up when it is in.
            play(stage, [{ opacity: 0 }, { opacity: 1 }], { duration: 300, delay: 200 });
        }
        marks.forEach(([c, [dx, dy]]) => play(c, [
            { transform: `translate(${dx}px, ${dy}px)`, opacity: 0 },
            { opacity: 1, offset: 0.2 },
            { transform: 'none', opacity: 1 },
        ], { duration: 520, delay: 100, easing: EASE_OUT }));
        // 3. ...and the key facts float in as a card, its parts clicking
        // into place one after another.
        play(card, [
            { transform: under ? 'translateY(40px)' : 'translateX(48px)', opacity: 0 },
            { transform: 'none', opacity: 1 },
        ], { duration: 440, delay: 260, easing: EASE_SNAP });
        [...(card ? card.children : [])].forEach((el, i) => play(el, [
            { transform: under ? 'translateY(12px)' : 'translateX(16px)', opacity: 0 },
            { transform: 'none', opacity: 1 },
        ], { duration: 300, delay: 320 + i * 30, easing: EASE_OUT }));
        play(closer, [{ transform: 'scale(0.8)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 240, delay: 360, easing: EASE_SNAP });
        play(hint, [{ opacity: 0 }, { opacity: 1 }], { duration: 260, delay: 460 });
        play(tools, [{ opacity: 0 }, { opacity: 1 }], { duration: 260, delay: 460 });
        settle(() => dialog.classList.remove('is-flying'));
    };

    const close = () => {
        if (!dialog.open || closing) return;
        closing = true;
        const finish = () => {
            home();
            dialog.close();
        };
        if (still()) {
            stop();
            finish();
            return;
        }
        // Still opening: run it back from where it is.
        if (anims.length) {
            dialog.classList.add('is-leaving');
            anims.forEach((a) => a.reverse());
            settle(finish);
            return;
        }
        const v = root.tx3d;
        const to = heroView.getBoundingClientRect();
        const bandRect = band.getBoundingClientRect();
        const bgRect = bg && bg.getBoundingClientRect();
        const marks = corners.map((c) => [c, cornerShift(c, c.getBoundingClientRect(), to)]);
        const under = below();
        // Back to the hero's exact view: the camera returns to the hero's
        // distance in step with the canvas (follow() below).
        let flight = null;
        if (v) {
            const [fx, fy] = mid(stage.getBoundingClientRect());
            const [tx, ty] = mid(to);
            flight = `translate(${tx - fx}px, ${ty - fy}px) scale(${to.height / port.clientHeight})`;
        }
        dialog.classList.add('is-flying', 'is-leaving');

        play(card, [
            { transform: 'none', opacity: 1 },
            { transform: under ? 'translateY(40px)' : 'translateX(48px)', opacity: 0 },
        ], { duration: 200, easing: EASE_IN });
        play(closer, [{ opacity: 1 }, { opacity: 0 }], { duration: 120 });
        play(hint, [{ opacity: 1 }, { opacity: 0 }], { duration: 120 });
        play(tools, [{ opacity: 1 }, { opacity: 0 }], { duration: 120 });
        if (flight) {
            const d = v.distances();
            v.follow(play(stage, [{ transform: 'none' }, { transform: flight }], { duration: 380, delay: 60, easing: EASE_IN_OUT }), d.now, d.home);
        } else {
            play(stage, [{ opacity: 1 }, { opacity: 0 }], { duration: 200 });
        }
        marks.forEach(([c, [dx, dy]]) => play(c, [
            { transform: 'none', opacity: 1 },
            { opacity: 1, offset: 0.8 },
            { transform: `translate(${dx}px, ${dy}px)`, opacity: 0 },
        ], { duration: 380, delay: 60, easing: EASE_IN_OUT }));
        if (bgRect) {
            play(bg, [
                { ...placeAt(bgRect, bgRect), opacity: 1, borderColor: 'rgba(255, 255, 255, 0.16)' },
                { opacity: 1, offset: 0.65 },
                { ...placeAt(bgRect, bandRect), opacity: 0, borderColor: 'rgba(255, 255, 255, 0)' },
            ], { duration: 460, easing: EASE_IN_OUT });
        }
        settle(finish);
    };

    root.addEventListener('tx3d:expand', open);
    // Like a native button: Enter opens on press, Space on release (a held
    // Space would otherwise repeat into the view's close button); held
    // keys never reach the page (a held Space would scroll it). Shortcuts
    // are not for us.
    const plain = (e) => e.target === root && !e.altKey && !e.ctrlKey && !e.metaKey;
    root.addEventListener('keydown', (e) => {
        if (!plain(e)) return;
        if (e.key === ' ') {
            e.preventDefault();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (!e.repeat) open(e);
        }
    });
    root.addEventListener('keyup', (e) => {
        if (plain(e) && e.key === ' ') open(e);
    });
    // A press on the stage (an orbit drag) must not focus the button: a
    // later Space would open the view instead of scrolling the page.
    root.addEventListener('mousedown', (e) => e.preventDefault());
    // Screen readers and voice control activate with a synthetic click:
    // on the element itself, or (WebKit) on the canvas at its centre,
    // either way with no click count.
    root.addEventListener('click', (e) => {
        if (e.target === root || e.detail === 0) open(e);
    });
    // View controls (APG toolbar: one tab stop, arrows move along it).
    const buttons = tools ? [...tools.querySelectorAll('button[data-tx3d-act]')] : [];
    const spinner = tools && tools.querySelector('[data-tx3d-act="spin"]');
    const STEP = Math.PI / 8;
    const syncSpin = () => {
        if (spinner && root.tx3d) spinner.setAttribute('aria-pressed', root.tx3d.spinning() ? 'true' : 'false');
    };
    root.addEventListener('tx3d:spin', syncSpin);
    // With reduced motion nothing turns by itself: nothing to stop.
    if (spinner && still()) spinner.hidden = true;
    if (tools) {
        tools.addEventListener('click', (e) => {
            const btn = e.target.closest('button[data-tx3d-act]');
            const v = root.tx3d;
            if (!btn || !v) return;
            const act = btn.dataset.tx3dAct;
            if (act === 'left') v.turn(-STEP);
            else if (act === 'right') v.turn(STEP);
            else if (act === 'up') v.tilt(-STEP);
            else if (act === 'down') v.tilt(STEP);
            else if (act === 'in') v.zoom(0.8);
            else if (act === 'out') v.zoom(1.25);
            else if (act === 'reset') v.reset();
            else if (act === 'spin') v.setSpin(!v.spinning());
        });
        // The tab stop stays on the control last used, however it was.
        tools.addEventListener('focusin', (e) => {
            const btn = e.target.closest('button[data-tx3d-act]');
            if (btn) buttons.forEach((b) => { b.tabIndex = b === btn ? 0 : -1; });
        });
        tools.addEventListener('keydown', (e) => {
            if (e.altKey || e.ctrlKey || e.metaKey) return;
            const shown = buttons.filter((b) => !b.hidden);
            const i = shown.indexOf(document.activeElement);
            if (i < 0) return;
            const j = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: shown.length - 1 }[e.key];
            if (j === undefined) return;
            e.preventDefault();
            const next = shown[(j + shown.length) % shown.length];
            shown.forEach((b) => { b.tabIndex = b === next ? 0 : -1; });
            next.focus();
        });
    }
    // Opened before the model was in: it moves over as soon as it is.
    root.addEventListener('tx3d:ready', () => {
        if (dialog.open && !closing) lend();
        syncSpin();
    });
    // No model after all: the hero stops offering the view, and a view
    // already open says so.
    const failed = () => {
        ['role', 'tabindex', 'aria-haspopup', 'aria-controls', 'aria-label'].forEach((a) => root.removeAttribute(a));
        const heroHint = root.querySelector('.tx-sp__model-hint');
        if (heroHint) heroHint.hidden = true;
        if (port) port.classList.add('is-err');
        if (tools) tools.hidden = true;
    };
    root.addEventListener('tx3d:error', failed);
    if (heroView.classList.contains('is-err')) failed();

    dialog.querySelectorAll('[data-tx-sp3d-close]').forEach((btn) => {
        btn.addEventListener('click', close);
    });
    // Enter held on the hero would repeat into the close button.
    dialog.addEventListener('keydown', (e) => {
        if (e.repeat && (e.key === 'Enter' || e.key === ' ')) e.preventDefault();
    });
    // A click on the veil, outside the box, closes it, but only when the
    // press started there too: a drag that starts on the model and ends
    // past the box would otherwise land its click on the dialog element.
    const outside = (e) => {
        const r = dialog.getBoundingClientRect();
        return e.target === dialog && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom);
    };
    let pressedVeil = false;
    dialog.addEventListener('pointerdown', (e) => {
        pressedVeil = outside(e);
    });
    dialog.addEventListener('click', (e) => {
        if (pressedVeil && outside(e)) close();
        pressedVeil = false;
    });
    // Escape (and Android's back): animated when the browser lets the
    // page hold the close; otherwise it closes at once.
    dialog.addEventListener('cancel', (e) => {
        if (!e.cancelable) return;
        e.preventDefault();
        close();
    });
    // However it closed: no animation left over, the viewer back home, and
    // focus back on the trigger. A pointer never focused it, so it only
    // gets the place in the tab order back (focus, then blur): Space then
    // still scrolls the page. Ignored if the view was reopened meanwhile.
    dialog.addEventListener('close', () => {
        if (dialog.open) return;
        stop();
        closing = false;
        dialog.classList.remove('is-entering', 'is-flying', 'is-leaving');
        if (vv) {
            vv.removeEventListener('resize', pin);
            vv.removeEventListener('scroll', pin);
        }
        dialog.style.translate = '';
        if (root.classList.contains('is-expanded')) home();
        const a = document.activeElement;
        if (root.hasAttribute('tabindex') && (!a || a === document.body || dialog.contains(a))) {
            root.focus({ preventScroll: true, focusVisible: false });
            if (openedBy === 'tx3d:expand') root.blur();
        }
    });
};

const init = () => {
    document.querySelectorAll('[data-tx3d]').forEach(gate);
    document.querySelectorAll('[data-tx3d-expand]').forEach(wireModal);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
